import React, { useMemo, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { ProductCard } from "./ProductCard";
import ProductDrawer from "./ProductDrawer";
import { trackEvent } from "../../../utils/analytics";

export function ProductStack({ products = [], gap = 3 }) {
    const theme = useTheme();

    const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
    const columns = isLgUp ? 3 : isSmUp ? 2 : 1;

    const total = products.length;
    const fullCount = Math.floor(total / columns) * columns;
    const main = products.slice(0, fullCount);
    const tail = products.slice(fullCount);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const selectedProduct = useMemo(() => {
        if (selectedIndex < 0) return null;
        return products[selectedIndex] || null;
    }, [products, selectedIndex]);

    const handleViewProduct = (product) => {
        const idx = products.findIndex((p) => p.id === product.id);
        setSelectedIndex(idx);
        setDrawerOpen(true);

        trackEvent("view_item", {
            items: [
                {
                    item_id: product.id,
                    item_name: product.name,
                    item_category: product.category,
                },
            ],
        });
    };

    const handleCloseDrawer = () => setDrawerOpen(false);

    const hasPrev = selectedIndex > 0;
    const hasNext = selectedIndex >= 0 && selectedIndex < products.length - 1;

    const handlePrev = () => {
        setSelectedIndex((i) => (i > 0 ? i - 1 : i));
    };

    const handleNext = () => {
        setSelectedIndex((i) => (i < products.length - 1 ? i + 1 : i));
    };

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gap,
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    alignItems: "stretch",
                }}
            >
                {main.map((p) => (
                    <Box key={p.id} sx={{ minWidth: 0, display: "flex" }}>
                        <ProductCard {...p} onViewProduct={handleViewProduct} />
                    </Box>
                ))}

                {tail.length > 0 && (
                    <Box
                        sx={{
                            gridColumn: "1 / -1",
                            display: "flex",
                            justifyContent: "center",
                            gap,
                        }}
                    >
                        {tail.map((p) => (
                            <Box
                                key={p.id}
                                sx={(t) => ({
                                    minWidth: 0,
                                    display: "flex",
                                    flex: "0 0 auto",
                                    width: `calc((100% - ${(columns - 1)} * ${t.spacing(gap)}) / ${columns})`,
                                })}
                            >
                                <ProductCard {...p} onViewProduct={handleViewProduct} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <ProductDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                product={selectedProduct}
                onPrev={handlePrev}
                onNext={handleNext}
                hasPrev={hasPrev}
                hasNext={hasNext}
            />
        </>
    );
}